import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import type { Instrument, Track, TrackDirection, MicStandType, ProvidedBy, StageRack } from '../types';
import { createTrack, STAND_LABELS, PROVIDED_BY_LABELS } from '../types';
import { rackCapacities, isChannelTakenForOutput, isInputListTaken, type RackUsage } from '../lib/rackUsage';
import { readableInk } from '../lib/contrast';

const MIC_TYPES = [
  'XLR',
  'SM58', 'SM57', 'Beta 58', 'Beta 57', 'SM7B', 'SM81',
  'MD421', 'C414', 'e604', 'e904', 'e602', 'D112',
  'NT5', 'KM184', 'R121', 'DI Box', 'Line In', 'Other',
];

const STAND_TYPES: MicStandType[] = ['none', 'small', 'medium', 'big', 'round_base', 'clip'];

const PROVIDED_BY: ProvidedBy[] = ['band', 'venue'];

// Console input-list channels offered in the picker. 0 = unpatched. Covers a
// realistic range (0-64) so odd/7/9/>32 channels are reachable via the UI.
const INPUT_LIST_CHANNELS = Array.from({ length: 65 }, (_, i) => i);

interface InstrumentEditorProps {
  instrument: Instrument;
  index: number;
  total: number;
  racks?: StageRack[];
  usage?: RackUsage;
  onChange: (instrument: Instrument) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  hasConflict?: boolean;
    conflictMessage?: string;
    /** Instrument names of the person being edited (replacement/guest sharing). */
    editedInstruments?: Set<string>;
    /** Map of every person's label → set of instrument names they play. */
    personInstruments?: Record<string, Set<string>>;
    /** Tags of the person being edited (replacement/guest sharing for crew). */
    editedTags?: Set<string>;
    /** Map of every person's label → set of tags they carry. */
    personTags?: Record<string, Set<string>>;
    /** Label of the person being edited — their OWN channels must always be
     *  greyed out (never reported as free by the replacement rule). */
    personLabel?: string;
    /** PATCH MARK favourites: colours used on OTHER instruments of the BAND
     *  (built by the edit page from all cards) — click to apply to this track. */
    personMarks?: string[];
    /** RENAME a favourite (2026-10-23, user): recolour EVERY track / monitor
     *  return of the band that carries `from` to `to` (persisted everywhere). */
    onRenameFavourite?: (from: string, to: string) => void;
    /** Shared-INHERITANCE (2026-10-23, user): for the instrument being edited,
     *  the OTHER person's same-named instrument tracks that carry a ticked
     *  `sharedWithReplacement` (main player declares the pair). When the
     *  edited track has no tick of its own, the checkbox shows the INHERITED
     *  state: checked + disabled (the decision lives on the main player's
     *  card). Shape: { ticked: boolean; by: string } | null. */
    inheritedShared?: { ticked: boolean; by: string } | null;
  }

export default function InstrumentEditor({
  instrument,
  index,
  total,
  racks = [],
  usage,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  hasConflict = false,
    conflictMessage,
    editedInstruments,
    personInstruments,
    editedTags,
    personTags,
    personLabel,
    personMarks,
    onRenameFavourite,
    inheritedShared,
  }: InstrumentEditorProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  // PATCH MARK popover (2026-10-23, v3): which track's colour picker is open.
  const [patchPopoverFor, setPatchPopoverFor] = useState<string | null>(null);
  // Viewport coordinates where the popover must appear (fixed without
  // coordinates lands off-screen on some layouts — user bug 2026-10-23).
  const [patchPopoverAt, setPatchPopoverAt] = useState<{ x: number; y: number } | null>(null);

  const updateInstrument = (field: keyof Instrument, value: any) => {
    onChange({ ...instrument, [field]: value });
  };

  const addTrack = () => {
    const trackNumber = instrument.tracks.length + 1;
    const newTrack = createTrack({ channel: trackNumber });
    updateInstrument('tracks', [...instrument.tracks, newTrack]);
  };

  const updateTrack = (trackId: string, patch: Partial<Track>) => {
    updateInstrument('tracks', instrument.tracks.map(t =>
      t.id === trackId ? { ...t, ...patch } : t
    ));
  };

  const removeTrack = (trackId: string) => {
    // Renumber remaining tracks starting at 1
    const remaining = instrument.tracks.filter(t => t.id !== trackId);
    const renumbered = remaining.map((t, i) => ({ ...t, channel: i + 1 }));
    updateInstrument('tracks', renumbered);
  };

  // PATCH MARK favourites (2026-10-23, user): colours already used on OTHER
  // instruments of the band (persisted on the tracks themselves — no extra
  // storage). The picker applies one with a click so a whole group of tracks
  // (e.g. everything going to the house stagebox) shares the same colour.
  const favMarks: string[] = personMarks || [];
  const patchMarks: string[] = (instrument.tracks || [])
    .map((t) => (t as any).patchMark)
    .filter((c: any): c is string => typeof c === 'string' && !!c)
    .filter((c: string, i: number, a: string[]) => a.indexOf(c) === i);

  return (
    <div className="instrument-editor">
      <div className="instrument-color-bar" style={{ backgroundColor: '#e94560' }} />

      <div className="instrument-content">
      {/* Header */}
      <div className="instrument-editor-header">
        <div className="instrument-editor-title">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="btn-icon"
            aria-label={expanded ? t('instrumentEditor.collapse') : t('instrumentEditor.expand')}
          >
            <svg className={`chevron ${expanded ? 'open' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <input
            type="text"
            value={instrument.name}
            onChange={e => updateInstrument('name', e.target.value)}
            placeholder={t('instrumentEditor.instrument', { n: index + 1 })}
            className="instrument-name-input"
          />
          <span className="track-count-label">
            {t('instrumentEditor.tracks', { count: instrument.tracks.length })}
          </span>
          {hasConflict && (
            <span className="conflict-warning" title={conflictMessage}>⚠</span>
          )}
        </div>
        <div className="instrument-editor-actions">
          <Button variant="ghost" size="sm" onClick={onMoveUp} disabled={index === 0}>↑</Button>
          <Button variant="ghost" size="sm" onClick={onMoveDown} disabled={index === total - 1}>↓</Button>
          <Button variant="ghost" size="sm" onClick={onRemove} className="btn-danger-icon">🗑</Button>
        </div>
      </div>

      {/* Wireless transmitter toggle — "does this instrument need a wireless
          link?". When ticked, the instrument appears in Rider Configuration →
          Audio & Backline (Wireless Transmitters) where the type/model/provider
          are set per transport. The signal still enters wired (DI/mic); the
          transmitter is a separate request. (2026-08-12) */}
      {expanded && (
        <div className="instrument-wireless" style={{ padding: '2px 12px 8px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-sm)' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!instrument.wireless?.enabled}
              onChange={(e) => updateInstrument('wireless', {
                enabled: e.target.checked,
                transmitterType: instrument.wireless?.transmitterType,
                notes: instrument.wireless?.notes,
                micProvider: instrument.wireless?.micProvider,
                receiverProvider: instrument.wireless?.receiverProvider,
              })}
            />
            <strong>{t('instrumentEditor.wirelessTransmitter')}</strong>
          </label>
          <span style={{ color: 'var(--text-dim)', opacity: 0.8 }}>
            {t('instrumentEditor.wirelessHint')}
          </span>
        </div>
      )}

      {/* Tracks — compact one-line-per-track */}
      {expanded && (
        <div className="tracks-section">
          {instrument.tracks.length === 0 ? (
            <p className="empty-state">{t('instrumentEditor.noTracks')}</p>
          ) : (
            <div className="tracks-list">
              {/* Column headers */}
              <div className="track-row track-header">
                <span className="th-track-n" title={t('instrumentEditor.trackNumber')}>N°</span>
                <span className="th-name">{t('instrumentEditor.name')}</span>
                <span className="th-rack" title={t('instrumentEditor.rackTitle')}>{t('instrumentEditor.rack')}</span>
                <span className="th-dir" title={t('instrumentEditor.directionTitle')}>{t('instrumentEditor.direction')}</span>
                <span className="th-io-num" title={t('instrumentEditor.ioChTitle')}>{t('instrumentEditor.ioCh')}</span>
                <span className="th-input" title={t('instrumentEditor.inputTitle')}>{t('instrumentEditor.input')}</span>
                <span className="th-patchmark" title={t('instrumentEditor.patchMarkTitle')}>🎨</span>
                <span className="th-shared" title={t('instrumentEditor.sharedTitle')}>{t('instrumentEditor.shared')}</span>
                <span className="th-mic">{t('instrumentEditor.mic')}</span>
                <span className="th-mic-from" title={t('instrumentEditor.micFromTitle')}>{t('instrumentEditor.micFrom')}</span>
                <span className="th-stand" title={t('instrumentEditor.standTitle')}>{t('instrumentEditor.stand')}</span>
                <span className="th-boom" title={t('instrumentEditor.boomTitle')}>{t('instrumentEditor.boom')}</span>
                <span className="th-provided" title={t('instrumentEditor.fromTitle')}>{t('instrumentEditor.from')}</span>
                <span className="th-destination" title={t('instrumentEditor.destinationTitle')}>{t('instrumentEditor.destination')}</span>
                <span className="th-notes">{t('instrumentEditor.notes')}</span>
                <span className="th-actions"></span>
              </div>

              {/* Tracks */}
              {instrument.tracks.map((track, tIdx) => (
                <div key={track.id} className="track-row track-data">
                  {/* N° piste — auto-numbered starting at 1 */}
                  <span className="td-track-n">{track.channel || tIdx + 1}</span>

                  {/* Nom */}
                  <input
                    type="text"
                    value={track.name}
                    onChange={e => updateTrack(track.id, { name: e.target.value })}
                    placeholder={t('instrumentEditor.kickSnarePh')}
                    className="td-input td-name"
                  />

                  {/* Rack this track patches into (input source or output
                      destination, depending on direction). */}
                  <select
                    value={track.rackId || ''}
                    onChange={e => updateTrack(track.id, { rackId: e.target.value })}
                    className="td-select td-rack"
                    title={t('instrumentEditor.rackTitle')}
                  >
                    <option value="">—</option>
                    {racks.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>

                  {/* Direction — input (into the mix) or output (return/send).
                      Output tracks patch into the rack's OUTPUT channels. */}
                  <select
                    value={track.direction || 'input'}
                    onChange={e => updateTrack(track.id, { direction: e.target.value as TrackDirection })}
                    className="td-select td-dir"
                    title={t('instrumentEditor.directionTitle')}
                  >
                    <option value="input">{t('instrumentEditor.directionInput')}</option>
                    <option value="output">{t('instrumentEditor.directionOutput')}</option>
                  </select>

                  {/* Channel number ON the selected rack — pick from the rack's
                      input (or output, for output tracks) channels, greyed out
                      where already used elsewhere */}
                  {(() => {
                    const rack = racks.find((r) => r.id === track.rackId);
                    const isOut = (track.direction || 'input') === 'output';
                    const { inputs, outputs } = rackCapacities(rack);
                    const capacity = isOut ? outputs : inputs;
                    const kind: 'inputs' | 'outputs' = isOut ? 'outputs' : 'inputs';
                    return (
                      <select
                        className="td-select td-io-num"
                        value={track.ioChannel || 0}
                        onChange={(e) => updateTrack(track.id, { ioChannel: parseInt(e.target.value) || 0 })}
                        title={isOut ? t('instrumentEditor.ioChTakenTitleOut') : t('instrumentEditor.ioChTakenTitle')}
                      >
                        <option value={0}>—</option>
                        {Array.from({ length: capacity }, (_, i) => i + 1).map((ch) => {
                          const occupant = isChannelTakenForOutput(
                            usage,
                            kind,
                            track.rackId,
                            ch,
                            track.rackId,
                            track.ioChannel,
                            editedInstruments,
                            personInstruments,
                            editedTags,
                            personTags,
                            personLabel,
                            !!track.sharedWithReplacement,
                          );
                          return (
                            <option key={ch} value={ch} disabled={!!occupant}>
                              {occupant ? `${ch} (${occupant})` : ch}
                            </option>
                          );
                        })}
                      </select>
                    );
                  })()}

                  {/* N° input list (console global input) — greyed out where
                      already assigned to another track (via usage.inputList),
                      excluding the channel being edited right now. */}
                  {/* LOT U1b — le patch mark est une couleur de DONNÉE : on ne la
                      tokenne pas, on choisit l'ENCRE lisible dessus (noir/blanc par
                      luminance). `#111` en dur rendait le n° illisible dès qu'un
                      musicien choisissait un patch sombre. */}
                  <select
                    className="td-select td-input-num"
                    style={track.patchMark ? { background: track.patchMark, color: readableInk(track.patchMark) } : undefined}
                    value={track.inputListChannel || 0}
                    onChange={(e) => updateTrack(track.id, { inputListChannel: parseInt(e.target.value) || 0 })}
                    title={t('instrumentEditor.inputTitle')}
                  >
                    <option value={0}>—</option>
                    {INPUT_LIST_CHANNELS.filter((ch) => ch > 0).map((ch) => {
                      const occupant = isInputListTaken(usage, ch, track.inputListChannel, editedInstruments, personInstruments, editedTags, personTags, personLabel, !!track.sharedWithReplacement);
                      return (
                        <option key={ch} value={ch} disabled={!!occupant}>
                          {occupant ? `${ch} (${occupant})` : ch}
                        </option>
                      );
                    })}
                  </select>

                  {/* PATCH MARK (2026-10-23, user v3): ONE button opens a popover
                      (favourites on top + native colour wheel below) — the
                      favourites column is gone from the grid (user: the table
                      is already too wide). Double-click the swatch = clear. */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="td-patchmark"
                      title={t('instrumentEditor.patchMarkTitle')}
                      onClick={(e) => {
                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const popW = 200, popH = 220;
                        let x = r.left;
                        let y = r.bottom + 6;
                        if (y + popH > window.innerHeight) y = Math.max(8, r.top - popH - 6);
                        if (x + popW > window.innerWidth) x = Math.max(8, window.innerWidth - popW - 8);
                        setPatchPopoverAt({ x, y });
                        const opening = patchPopoverFor !== track.id;
                        setPatchPopoverFor(track.id);
                        // FIRST click on a NEUTRAL swatch: open the native colour
                        // wheel DIRECTLY (user 2026-10-23 — seeing only a grey
                        // square suggests nothing happened). Once a colour is
                        // set, the click re-opens the popover (favourites).
                        if (opening && !track.patchMark) {
                          (e.currentTarget.nextElementSibling as HTMLInputElement | null)
                            ?.querySelector?.('input[type=color]');
                          const pop = document.querySelector(`[data-patch-popover-for="${track.id}"] input[type=color]`) as HTMLInputElement | null;
                          try { pop?.showPicker(); } catch { /* needs user gesture — the popover is the fallback */ }
                        }
                      }}
                      onDoubleClick={() => updateTrack(track.id, { patchMark: undefined })}
                      style={{ width: 30, height: 24, padding: 0, border: '1px solid var(--border)', borderRadius: 4, background: track.patchMark || 'var(--surface)', cursor: 'pointer' }}
                    />
                    {patchPopoverFor === track.id && patchPopoverAt && (
                      <div
                        data-patch-popover
                        data-patch-popover-for={track.id}
                        style={{
                          position: 'fixed', zIndex: 9999, top: patchPopoverAt.y, left: patchPopoverAt.x,
                          background: 'var(--surface, #211f1a)',
                          border: '1px solid var(--border)', borderRadius: 8, padding: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,.35)', minWidth: 180,
                        }}
                      >
                        {(patchMarks.length > 0 || favMarks.length > 0) && (
                          <>
                            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', marginBottom: 4 }}>{t('riderEditor.patchLegendsTitle')}</div>
                            {/* Many favourites (user 23.10: 15+ wanted): wrap on
                                rows, fixed swatch size — the popover grows
                                VERTICALLY, never sideways (viewport clamp holds). */}
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 208, marginBottom: 8 }}>
                              {[...patchMarks, ...favMarks.filter((c) => !patchMarks.includes(c))].map((c) => (
                                <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                  <button
                                    type="button"
                                    title={t('instrumentEditor.patchMarkFavTitle')}
                                    onClick={() => { updateTrack(track.id, { patchMark: c }); setPatchPopoverFor(null); }}
                                    style={{ width: 20, height: 20, borderRadius: 4, border: track.patchMark?.toLowerCase() === c ? '2px solid var(--text)' : '1px solid var(--border)', background: c, cursor: 'pointer', padding: 0 }}
                                  />
                                  {/* RENAME this favourite (2026-10-23, user): the
                                      mini-picker recolours EVERY track / monitor
                                      return of the band carrying this colour. */}
                                  {onRenameFavourite && (
                                    <input
                                      type="color"
                                      value={c}
                                      title={t('instrumentEditor.patchMarkRenameTitle')}
                                      onChange={(e) => onRenameFavourite(c, e.target.value)}
                                      style={{ width: 12, height: 20, padding: 0, border: '1px solid var(--border)', borderRadius: 3, background: 'transparent', cursor: 'pointer' }}
                                    />
                                  )}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                        <input
                          type="color"
                          value={track.patchMark || '#888888'}
                          onChange={(e) => updateTrack(track.id, { patchMark: e.target.value })}
                          onBlur={() => setPatchPopoverFor(null)}
                          style={{ width: '100%', height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                        />
                        <div style={{ marginTop: 6, textAlign: 'center' }}>
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => { updateTrack(track.id, { patchMark: undefined }); setPatchPopoverFor(null); }}>
                            {t('instrumentEditor.patchMarkClear')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Shared channel — tick when this track's rack channel /
                      console input is reused by a replacement or guest who
                      plays the same instrument (they never play at the same
                      time). Tick = the occupancy pickers let a channel already
                      used by a same-instrument person through; unticked =
                      strict occupancy (any used channel is greyed). (2026-09-20)
                      INHERITED (2026-10-23, user): when the OTHER same-instrument
                      player already ticked their track, this card shows the
                      INHERITED state — checked + disabled (the decision lives
                      on their card) + a tooltip naming them. */}
                  {(() => {
                    const own = !!track.sharedWithReplacement;
                    const inh = inheritedShared?.ticked && !own ? inheritedShared : null;
                    return (
                      <label
                        className="td-shared"
                        style={inh ? { opacity: 0.55 } : undefined}
                        title={inh ? t('instrumentEditor.sharedInheritedTitle', { by: inh.by }) : t('instrumentEditor.sharedTitle')}
                      >
                        <input
                          type="checkbox"
                          checked={own || !!inh}
                          disabled={!!inh}
                          onChange={e => updateTrack(track.id, { sharedWithReplacement: e.target.checked })}
                        />
                      </label>
                    );
                  })()}

                  {/* Micro */}
                  <input
                    type="text"
                    list="mic-types-list"
                    value={track.micType}
                    onChange={e => updateTrack(track.id, { micType: e.target.value })}
                    placeholder={t('instrumentEditor.sm57Ph')}
                    className="td-input td-mic"
                  />

                  {/* Mic provided by (band brings the mic, or venue provides it) */}
                  <select
                    value={track.micProvidedBy || 'band'}
                    onChange={e => updateTrack(track.id, { micProvidedBy: e.target.value as ProvidedBy })}
                    className="td-select td-mic-from"
                  >
                    {PROVIDED_BY.map(p => (
                      <option key={p} value={p}>{PROVIDED_BY_LABELS[p]}</option>
                    ))}
                  </select>

                  {/* Stand — size selector only (no band/venue) */}
                  <select
                    value={track.stand || 'none'}
                    onChange={e => updateTrack(track.id, { stand: e.target.value as MicStandType })}
                    className="td-select td-stand"
                  >
                    {STAND_TYPES.map(s => (
                      <option key={s} value={s}>{STAND_LABELS[s]}</option>
                    ))}
                  </select>

                  {/* Boom (only if a stand with a boom is selected — clip-on
                      microphones have no boom) */}
                  {track.stand && track.stand !== 'none' && track.stand !== 'clip' ? (
                    <label className="td-boom" title={track.withoutBoom ? t('instrumentEditor.withoutBoom') : t('instrumentEditor.withBoom')}>
                      <input
                        type="checkbox"
                        checked={!track.withoutBoom}
                        onChange={e => updateTrack(track.id, { withoutBoom: !e.target.checked })}
                      />
                      <span className="td-boom-label">{track.withoutBoom ? t('instrumentEditor.no') : t('instrumentEditor.yes')}</span>
                    </label>
                  ) : (
                    <span className="td-tx-empty">—</span>
                  )}

                  {/* Provided by (band brings the stand, or venue provides it) */}
                  <select
                    value={track.providedBy || 'band'}
                    onChange={e => updateTrack(track.id, { providedBy: e.target.value as ProvidedBy })}
                    className="td-select td-provided"
                  >
                    {PROVIDED_BY.map(p => (
                      <option key={p} value={p}>{PROVIDED_BY_LABELS[p]}</option>
                    ))}
                  </select>

                  {/* Destination — free text: who/where the (venue) stand is
                      for. Blank = the rider DERIVES it from the musician
                      carrying the instrument. Filled = overrides the derived
                      value in the Mic Stands table. (2026-10-07, v0.651) */}
                  <input
                    type="text"
                    value={track.destination || ''}
                    onChange={e => updateTrack(track.id, { destination: e.target.value })}
                    placeholder={t('instrumentEditor.destinationPh')}
                    title={t('instrumentEditor.destinationTitle')}
                    className="td-input td-destination"
                  />

                  {/* Notes */}
                  <input
                    type="text"
                    value={track.notes}
                    onChange={e => updateTrack(track.id, { notes: e.target.value })}
                    placeholder="—"
                    className="td-input td-notes"
                  />

                  {/* Actions */}
                  <button
                    type="button"
                    onClick={() => removeTrack(track.id)}
                    className="td-remove"
                    aria-label={t('instrumentEditor.removeTrack')}
                  >🗑</button>
                </div>
              ))}
            </div>
          )}

          <div className="tracks-footer">
            <Button variant="secondary" size="sm" onClick={addTrack}>{t('instrumentEditor.addTrack')}</Button>
          </div>
        </div>
      )}
      </div>
      {/* Single shared datalist for mic-type autocomplete (must live OUTSIDE
          the track grid — a datalist inside .track-row became a grid child
          and shifted every column from Connection on). (2026-08-09) */}
      <datalist id="mic-types-list">
        {MIC_TYPES.map(mic => <option key={mic} value={mic} />)}
      </datalist>
    </div>
  );
}
